/**
 * Adapter to transform v2 schedule data to calendar-compatible format
 */

/**
 * Transform v2 schedule format to calendar events format
 * @param {Object} v2Schedule - Schedule data from v2 API
 * @returns {Array} - Array of calendar-compatible day schedules
 */
export function transformV2ScheduleToCalendarFormat(v2Schedule) {
  if (!v2Schedule) {
    return [];
  }
  
  if (!v2Schedule.itinerary) {
    return [];
  }
  
  if (!v2Schedule.itinerary.schedule) {
    return [];
  }

  // Group stops by day
  const dayGroups = {};
  
  v2Schedule.itinerary.schedule.forEach(stop => {
    // First check if stop has a 'day' property (new enhanced schema)
    const dayStr = stop.day || stop.date;
    const dayNumber = stop.day_number || 1;
    
    if (!dayGroups[dayNumber]) {
      dayGroups[dayNumber] = {
        day: dayStr || '', // Use the day string from the stop if available
        agenda: []
      };
    }
    
    // Parse time slot to get start time
    let startTime = '09:00'; // Default
    const timeSlot = stop.time || stop.time_slot; // Handle both 'time' and 'time_slot'
    
    if (timeSlot) {
      const match = timeSlot.match(/(\d{1,2}):(\d{2})\s*([AP]M)/i);
      if (match) {
        let hours = parseInt(match[1]);
        const minutes = match[2];
        const period = match[3].toUpperCase();
        
        // Convert to 24-hour format
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        startTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
      }
    }
    
    // Create agenda item
    const agendaItem = {
      time: startTime,
      type: stop.type || 'customer',
      customer_name: stop.name || stop.customer_name || 'Unknown Customer',
      customer_id: stop.customer_id,
      stop_id: stop.stop_id,
      address: stop.address,
      city: stop.city,
      state: stop.state,
      coordinates: stop.coordinates || {
        lat: stop.lat,
        lng: stop.lng || stop.lon
      },
      estimated_onsite: stop.visit_duration || '1 hour',
      notes: stop.notes || '',
      opportunities: stop.opportunities,
      opportunity_summary: stop.opportunity_summary,
      enhanced_opportunities: stop.enhanced_opportunities || [],
      revenue_lifetime: stop.revenue_lifetime,
      status: stop.status || 'PENDING',
      original_time_slot: timeSlot,
      stop_order: stop.stop_order,
      bm_notes: stop.bm_notes,
      rsm_notes_history: stop.rsm_notes_history,
      // Include all the stop data for access in components
      ...stop
    };
    
    dayGroups[dayNumber].agenda.push(agendaItem);
  });
  
  // Convert to array and set dates
  const calendarSchedule = [];
  const startDate = v2Schedule.metadata?.start_date || v2Schedule.start_date || new Date().toISOString().split('T')[0];
  
  
  Object.keys(dayGroups).sort((a, b) => parseInt(a) - parseInt(b)).forEach(dayNumber => {
    const dayData = dayGroups[dayNumber];
    
    // Calculate date for this day
    const date = new Date(startDate);
    date.setDate(date.getDate() + (parseInt(dayNumber) - 1));
    dayData.day = date.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Sort agenda items by time
    dayData.agenda.sort((a, b) => {
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });
    
    calendarSchedule.push(dayData);
  });
  
  
  return calendarSchedule;
}

/**
 * Transform v2 itinerary days format to calendar format
 * This handles the newer v2 format with explicit days array
 * @param {Object} v2Itinerary - Itinerary data from v2 API
 * @returns {Array} - Array of calendar-compatible day schedules
 */
export function transformV2ItineraryToCalendarFormat(v2Itinerary) {
  if (!v2Itinerary || !v2Itinerary.days) {
    return [];
  }

  return v2Itinerary.days.map(day => {
    const agenda = day.stops.map((stop, index) => {
      // Parse time from time slot
      let startTime = '09:00'; // Default
      if (stop.time) {
        const match = stop.time.match(/(\d{1,2}):(\d{2})\s*([AP]M)/i);
        if (match) {
          let hours = parseInt(match[1]);
          const minutes = match[2];
          const period = match[3].toUpperCase();
          
          // Convert to 24-hour format
          if (period === 'PM' && hours !== 12) {
            hours += 12;
          } else if (period === 'AM' && hours === 12) {
            hours = 0;
          }
          
          startTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
        }
      }
      
      return {
        time: startTime,
        type: 'customer',
        customer_name: stop.customer_name,
        address: stop.address,
        city: stop.city || '',
        state: stop.state || '',
        estimated_onsite: stop.visit_duration || '1 hour',
        notes: stop.notes || '',
        status: 'PENDING',
        original_time_slot: stop.time,
        stop_order: index + 1
      };
    });
    
    return {
      day: day.date,
      agenda: agenda
    };
  });
}

/**
 * Check if schedule data is v2 format
 * @param {Object} scheduleData - Schedule data to check
 * @returns {boolean} - True if v2 format
 */
export function isV2ScheduleFormat(scheduleData) {
  // Check for v2 specific fields
  return scheduleData && (
    // Has time_slot in schedule items
    (scheduleData.itinerary?.schedule?.[0]?.time_slot) ||
    // Has time in schedule items (enhanced schema)
    (scheduleData.itinerary?.schedule?.[0]?.time) ||
    // Has day_number in schedule items
    (scheduleData.itinerary?.schedule?.[0]?.day_number) ||
    // Has enhanced_opportunities (enhanced schema)
    (scheduleData.itinerary?.schedule?.[0]?.enhanced_opportunities) ||
    // Has days array structure
    (scheduleData.itinerary?.days)
  );
}

/**
 * Universal adapter that handles both v1 and v2 formats
 * @param {Object} scheduleData - Schedule data in either format
 * @returns {Array} - Array of calendar-compatible day schedules
 */
export function adaptScheduleToCalendarFormat(scheduleData) {
  if (!scheduleData) {
    return [];
  }
  
  // Check if it's already in calendar format (has day/agenda structure)
  if (Array.isArray(scheduleData)) {
    if (scheduleData.length > 0 && scheduleData[0]?.day && scheduleData[0]?.agenda) {
      return scheduleData; // Already in correct format
    }
  }
  
  // Check for enhanced API format where schedule is already grouped by days
  if (scheduleData.itinerary?.schedule && Array.isArray(scheduleData.itinerary.schedule)) {
    const firstItem = scheduleData.itinerary.schedule[0];
    
    // Check if it's already in day/agenda format (enhanced API might return this)
    if (firstItem?.day && firstItem?.agenda) {
      return scheduleData.itinerary.schedule;
    }
  }
  
  // Check for v2 format
  if (isV2ScheduleFormat(scheduleData)) {
    if (scheduleData.itinerary?.days) {
      return transformV2ItineraryToCalendarFormat(scheduleData.itinerary);
    } else {
      return transformV2ScheduleToCalendarFormat(scheduleData);
    }
  }
  
  // Handle v1 format or direct schedule array
  if (scheduleData.itinerary?.schedule && Array.isArray(scheduleData.itinerary.schedule)) {
    const firstItem = scheduleData.itinerary.schedule[0];
    
    // If it's a flat array of stops (v2 format), transform it
    if (firstItem && (firstItem.time_slot || firstItem.day_number || firstItem.time)) {
      return transformV2ScheduleToCalendarFormat(scheduleData);
    }
  }
  
  return [];
}